import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './PublicNavbar.css';
import logo from '../../images/logo no bg-03.png';
import { Dropdown } from "react-bootstrap";
import EditProfileCorporate from './nav/EditProfileCorporate';
import EditProfileCorporateUser from './nav/EditProfileCorporateUser';
import LogoutPage from './nav/Logout';

const productsModules = [
  {
    name: 'Tasks',
    path: '/products/task',
    description: 'Organize, assign, and track tasks with projects and team collaboration.',
  },
  {
    name: 'Documents',
    path: '/products/documents',
    description: 'Store, organize, and share documents securely with your team.',
  },
  {
    name: 'Credentials Vault',
    path: '/products/credentials-vault',
    description: 'Encrypt and manage team passwords with corporate and personal vaults.',
  },
];

const PublicNavbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const productsDropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);

    // Load user from localStorage with more robust checking
    const loadUser = () => {
      const rememberMe = localStorage.getItem("rememberMe") === "true";
      let accessToken = null;
      
      if (rememberMe) {
        accessToken = localStorage.getItem("accessToken");
      } else {
        accessToken = sessionStorage.getItem("accessToken");
        const tempAccessToken = localStorage.getItem("tempAccessToken");
        if (tempAccessToken) {
          accessToken = tempAccessToken;
          sessionStorage.setItem("accessToken", tempAccessToken);
        }
      }
      
      const userData = localStorage.getItem("user");
      
      if (accessToken && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        } catch (error) {
          console.error("Error parsing user data:", error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    // Load user immediately
    loadUser();

    // Also listen for storage changes to update user state
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'accessToken' || e.key === 'logout') {
        loadUser();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (productsDropdownRef.current && !productsDropdownRef.current.contains(e.target)) {
        setProductsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`public-navbar ${scrolled ? 'scrolled' : ''}`}
      style={{
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div className="public-navbar-container">
        <Link to="/" className="public-navbar-logo">
          <img
            src={logo}
            alt="Logo"
            className="public-navbar-logo-img"
            style={{ height: '56px' }}
          />
        </Link>

        <button
          className={`public-navbar-toggle ${open ? 'active' : ''}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle navigation"
        >
          <span className="toggle-line"></span>
          <span className="toggle-line"></span>
          <span className="toggle-line"></span>
        </button>

        <div className={`public-navbar-links ${open ? 'open' : ''}`}>
          <Link
            to="/about-us"
            className="public-navbar-link"
            onClick={() => setOpen(false)}
          >
            About Us
          </Link>
          <Dropdown
            className="products-dropdown-nav"
            ref={productsDropdownRef}
            show={productsDropdownOpen}
            onToggle={() => setProductsDropdownOpen(!productsDropdownOpen)}
          >
            <Dropdown.Toggle
              as="span"
              className="public-navbar-link"
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              Products
            </Dropdown.Toggle>
            <Dropdown.Menu align="start" className="products-dropdown-menu">
              {productsModules.map((module) => (
                <Dropdown.Item
                  key={module.path}
                  as={Link}
                  to={module.path}
                  className="products-dropdown-item"
                  onClick={() => {
                    setOpen(false);
                    setProductsDropdownOpen(false);
                  }}
                >
                  <div className="products-dropdown-item-title">{module.name}</div>
                  <div className="products-dropdown-item-desc">{module.description}</div>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Link
            to="/pricing"
            className="public-navbar-link"
            onClick={() => setOpen(false)}
          >
            Pricing
          </Link>
          <Link
            to="/contact-us"
            className="public-navbar-link"
            onClick={() => setOpen(false)}
          >
            Contact Us
          </Link>
          {user && (
            <Link
              to="/task/tasklist"
              className="public-navbar-link"
              onClick={() => setOpen(false)}
            >
              Dashboard
            </Link>
          )}

          {!user ? (
            <>
              <Link
                to="/login"
                className="public-navbar-link"
                onClick={() => setOpen(false)}
              >
                Login
              </Link>
              <Link
                to="/register"
                className="public-navbar-link public-navbar-link-cta"
                onClick={() => setOpen(false)}
              >
                Get Started
              </Link>
            </>
          ) : (
            <Dropdown className="nav-item dropdown header-profile" style={{ marginLeft: '0px', paddingLeft: '0px' }} ref={dropdownRef} show={dropdownOpen} onToggle={() => setDropdownOpen(!dropdownOpen)}>
              <Dropdown.Toggle variant="" as="a" className="nav-link i-false c-pointer"
                role="button" data-toggle="dropdown"
              >
                {/* Replace profile image with a generic avatar circle with initials */}
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 16,
                  color: '#555',
                  marginRight: 8
                }}>
                  {(() => {
                    const displayName = user?.name || user?.full_name || user?.FullName || '';
                    return displayName
                      ? displayName.split(' ').map(n => n[0]).join('').toUpperCase()
                      : '?';
                  })()}
                </div>
              </Dropdown.Toggle>
              <Dropdown.Menu align="right" className="mt-3  mt-lg-0 dropdown-menu dropdown-menu-end">
                {/* Profile Info */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', minWidth: 220 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2 }}>{user?.name || user?.full_name || user?.FullName || 'User'}</div>
                  <div style={{ fontSize: 13, color: '#888' }}>{user && (() => { try { return user.email || ''; } catch { return ''; } })()}</div>
                </div>
                {/* Edit Profile Option */}
                {(() => {
                  let type = null;
                  try {
                    type = user && user.type;
                  } catch { }
                  if (type === 'corporate') {
                    return <>
                      <EditProfileCorporate onEdit={() => {/* handle edit corporate */ }} />
                      <Dropdown.Item className="public-dropdown-item" as={Link} to="/task/tasklist">Tasks</Dropdown.Item>
                      <Dropdown.Item className="public-dropdown-item" as={Link} to="/documents/dashboard">Documents</Dropdown.Item>
                      <Dropdown.Item className="public-dropdown-item" as={Link} to="/credential-vault/credentialVaultList">Credentials Vault</Dropdown.Item>
                      <Dropdown.Item className="public-dropdown-item" as={Link} to="/changePassword">Change Password</Dropdown.Item>
                    </>;
                  } else if (type === 'user') {
                    return <>
                      <EditProfileCorporateUser onEdit={() => {/* handle edit corporate user */ }} />
                      <Dropdown.Item className="public-dropdown-item" as={Link} to="/task/tasklist">Tasks</Dropdown.Item>
                      <Dropdown.Item className="public-dropdown-item" as={Link} to="/documents/dashboard">Documents</Dropdown.Item>
                      <Dropdown.Item className="public-dropdown-item" as={Link} to="/credential-vault/credentialVaultList">Credentials Vault</Dropdown.Item>
                      <Dropdown.Item className="public-dropdown-item" as={Link} to="/changePassword">Change Password</Dropdown.Item>
                    </>;
                  } else {
                    return null;
                  }
                })()}

                {/* Logout Option */}
                <div onClick={() => setUser(null)}>
                  <LogoutPage />
                </div>

              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>
    </nav>
  );
};

export default PublicNavbar;
